import React, { useEffect, useMemo, useState } from "react"
import ReactDOM from "react-dom"
import "./style.css"
import FadeInUp from "../animate/FadeInUp"
import { FeatureColumn, FeatureColumnWrapper } from "./feature.style"
import useLang from "../../hooks/useLang"
import { Icon } from "react-icons-kit"
import { pencilSquareO } from "react-icons-kit/fa/pencilSquareO"
import { desktop } from "react-icons-kit/fa/desktop"
import { print } from "react-icons-kit/fa/print"
import { shield } from "react-icons-kit/fa/shield"
import { bullhorn } from "react-icons-kit/fa/bullhorn"
import { gift } from "react-icons-kit/fa/gift"
import { cogs } from "react-icons-kit/fa/cogs"
import { calendar } from "react-icons-kit/fa/calendar"
import { check } from "react-icons-kit/fa/check"
import { times } from "react-icons-kit/fa/times"
import { Box } from "theme-ui"
import { servicesApi } from "../../services/api"

const icons = {
  pencilSquareO,
  desktop,
  print,
  shield,
  bullhorn,
  gift,
  cogs,
  calendar,
  check,
}

const FALLBACK_ICON = pencilSquareO

const SERVICE_ORDER = [
  "branding",
  "design",
  "printing",
  "security",
  "marketing",
  "gifts",
  "development",
  "events"
]

const SERVICE_ICON_MAP = {
  branding:    "pencilSquareO",
  design:      "desktop",
  printing:    "print",
  security:    "shield",
  marketing:   "bullhorn",
  gifts:       "gift",
  development: "cogs",
  events:      "calendar",
}

const truncateWords = (text = "", limit = 18) => {
  const words = String(text).trim().split(/\s+/)
  if (words.length <= limit) return text
  return `${words.slice(0, limit).join(" ")}...`
}

const Features = () => {
  const isAr = useLang() === "ar"
  const serviceTitle = isAr ? "خدماتنا" : "Our Services"

  const [apiServices, setApiServices] = useState([])
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)
  const [modalContent, setModalContent] = useState(null)
  const [mounted, setMounted] = useState(false)

  const labels = useMemo(
    () => ({
      readMore: isAr ? "اقرأ المزيد" : "Read More",
      close: isAr ? "إغلاق" : "Close",
    }),
    [isAr]
  )

  useEffect(() => {
    let cancelled = false
    setLoading(true)

    servicesApi.index()
      .then(data => {
        if (!cancelled) {
          setApiServices(data || [])
          setLoading(false)
        }
      })
      .catch(err => {
        console.error("Failed to fetch services:", err)
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [])

  useEffect(() => setMounted(true), [])

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    const handleEsc = e => e.key === "Escape" && (setOpen(false), setModalContent(null))
    document.addEventListener("keydown", handleEsc)
    return () => {
      document.body.style.overflow = prev
      document.removeEventListener("keydown", handleEsc)
    }
  }, [open])

  const closeModal = () => {
    setOpen(false)
    setModalContent(null)
  }

  const getIcon = (iconName) => icons[iconName] || FALLBACK_ICON

  const getServiceTypeText = (service) => {
    const type = service?.service_type

    if (!type) return ""

    if (typeof type === "object") {
      return type.key || type.name_en || type.name_ar || ""
    }

    return String(type)
  }

  const getServiceSortKey = (service) => {
    const typeText = getServiceTypeText(service)
    const titleText = service?.title_en || service?.title_ar || ""
    const joined = `${typeText} ${titleText}`.toLowerCase()

    const matched = SERVICE_ORDER.find(key => joined.includes(key))

    return matched || joined.trim()
  }

  const sortedEdges = useMemo(() => {
  if (!apiServices || apiServices.length === 0) return []

  const sorted = [...apiServices].sort((a, b) => {
    const keyA = getServiceSortKey(a)
    const keyB = getServiceSortKey(b)

    const indexA = SERVICE_ORDER.indexOf(keyA)
    const indexB = SERVICE_ORDER.indexOf(keyB)

    if (indexA !== -1 && indexB !== -1) return indexA - indexB
    if (indexA === -1 && indexB !== -1) return 1
    if (indexA !== -1 && indexB === -1) return -1

    return keyA.localeCompare(keyB)
  })

  return sorted.map(service => {
    const sortKey = getServiceSortKey(service)

    const title = isAr
      ? service.title_ar || service.title_en || getServiceTypeText(service)
      : service.title_en || service.title_ar || getServiceTypeText(service)

    const description = isAr
      ? service.description_ar || service.description_en || ""
      : service.description_en || service.description_ar || ""

    const iconName =
      SERVICE_ICON_MAP[sortKey] ||
      SERVICE_ICON_MAP[getServiceTypeText(service).toLowerCase()] ||
      "pencilSquareO"

    return {
      node: {
        frontmatter: {
          title,
          description,
          icon: iconName,
          features: service.features || [],
          serviceKey: String(service.id),
        },
      },
    }
  })
}, [apiServices, isAr])

  const modalMarkup = open && modalContent ? (
    <div className="feature-modal" role="dialog" aria-modal="true">
      <div className="feature-modal__backdrop" onClick={closeModal} />
      <div className="feature-modal__panel">
        <div className="feature-modal__header">
          <div className="feature-modal__title">
            <span className="feature-modal__icon">
              <Icon size={24} icon={getIcon(modalContent.icon)} />
            </span>
            <span>{modalContent.title}</span>
          </div>
          <button 
            type="button" 
            className="feature-modal__close" 
            onClick={closeModal} 
            aria-label={labels.close}
          >
            <Icon 
              icon={times} 
              size={20} 
              className="feature-modal__close-icon"
            />
          </button>
        </div>
        <div
          className="feature-modal__body"
          dangerouslySetInnerHTML={{ __html: modalContent.description }}
        />
      </div>
    </div>
  ) : null

  if (loading) {
    return (
      <section className="b-features-columns-holder features-section">
        <div className="text-center">
          <Box className="about-badge-wrapper">
            <span className="about-badge">
              <span className="about-badge-dot" />
              {isAr ? "اكتشف خدماتنا المميزة" : "Discover Our Exceptional Services"}
            </span>
          </Box>
        </div>
        <h2 className="b-upper-title text-center-service-title">{serviceTitle}</h2>
        <div className="features-section__inner container">
          <div style={{ padding: "40px", textAlign: "center", color: "#999" }}>
            {isAr ? "جارٍ التحميل..." : "Loading..."}
          </div>
        </div>
      </section>
    )
  }

  if (!sortedEdges || sortedEdges.length === 0) {
    return (
      <section className="b-features-columns-holder features-section">
        <div className="text-center">
          <Box className="about-badge-wrapper">
            <span className="about-badge">
              <span className="about-badge-dot" />
              {isAr ? "اكتشف خدماتنا المميزة" : "Discover Our Exceptional Services"}
            </span>
          </Box>
        </div>

        <h2 className="b-upper-title text-center-service-title">{serviceTitle}</h2>

        <div className="features-section__inner container">
          <div style={{ padding: "40px", textAlign: "center", color: "#999" }}>
            <p>⚠️ {isAr ? "لم يتم العثور على الخدمات" : "No services data available"}</p>
            <p style={{ fontSize: "12px", marginTop: "10px" }}>
              {isAr ? "تحقق من اتصال API" : "Check API connection"}
            </p>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="b-features-columns-holder features-section">
      <div className="text-center">
        <Box className="about-badge-wrapper">
          <span className="about-badge">
            <span className="about-badge-dot" />
            {isAr ? "اكتشف خدماتنا المميزة" : "Discover Our Exceptional Services"}
          </span>
        </Box>
      </div>

      <h2 className="b-upper-title text-center-service-title">{serviceTitle}</h2>

      <div className="features-section__inner container">
        <FeatureColumnWrapper>
          {sortedEdges.map(({ node: { frontmatter: { description, icon, title, features = [], serviceKey } } }, index) => (
            <FeatureColumn key={serviceKey || title}>
              <FadeInUp delay={index * 50}>
                <article className="features-card">
                  <div className="features-card__header">
                    <div className="features-card__icon-wrap">
                      <div className="features-column-icon">
                        <Icon size={24} icon={getIcon(icon)} />
                      </div>
                    </div>
                    <h3 className="features-column-title">{title}</h3>
                  </div>

                  <div
                    className="features-column-text"
                    dangerouslySetInnerHTML={{ __html: truncateWords(description) }}
                  />

                  {features.length > 0 && (
                    <ul className="features-list">
                      {features.map((feature, idx) => (
                        <li key={idx} className="features-list-item">
                          <Icon size={14} icon={check} className="features-list-icon" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <button
                    type="button"
                    className="features-btn-started"
                    onClick={() => { setModalContent({ title, description, icon }); setOpen(true) }}
                    aria-label={`${labels.readMore}: ${title}`}
                  >
                    {labels.readMore}
                  </button>
                </article>
              </FadeInUp>
            </FeatureColumn>
          ))}
        </FeatureColumnWrapper>
      </div>

      {mounted && ReactDOM.createPortal(modalMarkup, document.body)}
    </section>
  )
}

export default Features
